import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { C } from "../alignment-advantage/shared/constants";
import FaqAccordion from "./FaqAccordion";
import AppNavTourOverlay from "../tour/AppNavTourOverlay";
import { FAQ_PUBLIC_ITEMS } from "../../data/faqContent";
import { useFaqItems } from "../../hooks/useFaqItems";

const FAB_Z = 50;
const PANEL_Z = 55;
const FAB_LABEL = "Need help?";

export interface FaqHelpFabProps {
  /** In-app shell: tier-gated FAQ + tour. Public auth: login FAQ only. */
  variant: "in-app" | "public";
  /** Opens mobile nav drawer before the app navigation tour */
  onOpenMobileNav?: () => void;
}

const IconHelpSmall = (
  <i
    className="fa-solid fa-circle-question text-sm leading-none shrink-0"
    aria-hidden="true"
  />
);

const IconClose = (
  <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    className="w-5 h-5"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M6 18L18 6M6 6l12 12"
    />
  </svg>
);

/**
 * Floating help button (bottom-right) with a slide-over FAQ panel.
 */
const FaqHelpFab: React.FC<FaqHelpFabProps> = ({
  variant,
  onOpenMobileNav,
}) => {
  const location = useLocation();
  const inAppItems = useFaqItems();
  const faqItems = variant === "public" ? FAQ_PUBLIC_ITEMS : inAppItems;

  const [panelOpen, setPanelOpen] = useState(false);
  const [tourActive, setTourActive] = useState(false);

  const closePanel = useCallback((): void => {
    setPanelOpen(false);
  }, []);

  const openPanel = useCallback((): void => {
    setPanelOpen(true);
  }, []);

  useEffect(() => {
    if (!panelOpen) return;

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        closePanel();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [panelOpen, closePanel]);

  useEffect(() => {
    if (!panelOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [panelOpen]);

  if (location.pathname === "/faq") {
    return null;
  }

  if (typeof document === "undefined") {
    return null;
  }

  const ensureNavVisible = (): void => {
    const isMobile = window.matchMedia("(max-width: 767px)").matches;
    if (isMobile) {
      onOpenMobileNav?.();
    }
  };

  const startTour = (): void => {
    closePanel();
    setTourActive(true);
  };

  return createPortal(
    <>
      <button
        type="button"
        onClick={openPanel}
        className="fixed flex items-center justify-center gap-2 px-4 py-3 min-h-[3.25rem] rounded-full shadow-lg transition-transform hover:scale-105 active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 text-sm font-bold tracking-wide"
        style={{
          zIndex: FAB_Z,
          right: "max(1rem, env(safe-area-inset-right, 0px))",
          bottom: "max(1rem, env(safe-area-inset-bottom, 0px))",
          background: `linear-gradient(135deg, ${C.coral}, ${C.coralDark})`,
          color: C.white,
          boxShadow: `0 8px 24px ${C.coral}55`,
        }}
        aria-label={FAB_LABEL}
        aria-expanded={panelOpen}
        aria-haspopup="dialog"
      >
        {IconHelpSmall}
        <span>{FAB_LABEL}</span>
      </button>

      {panelOpen && (
        <>
          <button
            type="button"
            className="fixed inset-0 bg-black/45"
            style={{ zIndex: PANEL_Z }}
            onClick={closePanel}
            aria-label="Close help panel"
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="faq-help-panel-title"
            className="fixed inset-y-0 right-0 flex flex-col w-full max-w-md shadow-2xl"
            style={{
              zIndex: PANEL_Z + 1,
              background: C.cream,
            }}
          >
            <header
              className="shrink-0 px-4 py-4 border-b border-black/10"
              style={{
                paddingTop: "max(1rem, env(safe-area-inset-top, 0px))",
                background: `linear-gradient(180deg, ${C.navy} 0%, ${C.navyMid} 100%)`,
              }}
            >
              <p
                className="text-[10px] font-bold uppercase tracking-[0.2em] mb-1"
                style={{ color: C.coral }}
              >
                Help
              </p>
              <div className="flex items-center gap-2 min-w-0">
                <h2
                  id="faq-help-panel-title"
                  className="text-lg font-bold text-white shrink-0"
                  style={{ fontFamily: "Georgia,'Times New Roman',serif" }}
                >
                  FAQ
                </h2>
                <div className="flex-1 min-w-2" aria-hidden="true" />
                {variant === "in-app" && (
                  <button
                    type="button"
                    onClick={startTour}
                    className="shrink min-w-0 inline-flex items-center justify-center rounded-lg px-2 py-1.5 sm:px-3.5 sm:py-2 text-[10px] sm:text-xs font-semibold text-white shadow-sm transition-opacity hover:opacity-90 max-w-[9.5rem] sm:max-w-none leading-tight text-center"
                    style={{
                      background: `linear-gradient(90deg, ${C.coral}, ${C.coralDark})`,
                    }}
                  >
                    Take a quick tour
                  </button>
                )}
                <button
                  type="button"
                  onClick={closePanel}
                  className="shrink-0 flex items-center justify-center w-9 h-9 rounded-lg"
                  style={{
                    color: "rgba(255,255,255,0.85)",
                    background: "rgba(255,255,255,0.1)",
                  }}
                  aria-label="Close FAQ panel"
                >
                  {IconClose}
                </button>
              </div>
              <p className="text-xs mt-1 pr-11" style={{ color: "rgba(255,255,255,0.55)" }}>
                {variant === "public"
                  ? "Sign-in and account setup."
                  : "Guides for Dashboard, charts, and Settings."}
              </p>
            </header>

            <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 py-4 pb-8 bg-theme-surface-cream dark:bg-surface-dark">
              <FaqAccordion items={faqItems} />

              <p className="mt-6 text-center text-sm">
                <Link
                  to="/faq"
                  onClick={closePanel}
                  className="font-semibold underline-offset-2 hover:underline"
                  style={{ color: C.coralDark }}
                >
                  Open full FAQ page
                </Link>
              </p>
            </div>
          </div>
        </>
      )}

      {variant === "in-app" && (
        <AppNavTourOverlay
          active={tourActive}
          onClose={() => setTourActive(false)}
          onEnsureNavVisible={ensureNavVisible}
        />
      )}
    </>,
    document.body
  );
};

export default FaqHelpFab;
